import React, { useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TailSpin } from "react-loader-spinner";
import { MdOutlineKeyboardArrowRight } from "react-icons/md";

import Navbar from "../../components/Navbar";
import Sidebar from "../../components/Sidebar";
import { PROD_LINK, STAGING_LINK } from "../../constant";
import { IS_STAGING } from "../../constant/api";
import { useAdminBusinessSummaryQuery } from "../../lib/api/useAdminBusinessSummaryQuery";
import { useAdminBusinessTransactionsQuery } from "../../lib/api/useAdminBusinessTransactionsQuery";
import { formatOrdinalDateTime } from "../users/clientsTable";
import type { MerchantRecord } from "../users/merchantsTable";
import MerchantSummarySections from "./MerchantSummarySections";
import MerchantTransactionsPanel from "./MerchantTransactionsPanel";
import { businessStatusBadgeClass } from "./merchantTransactionsTable";

type TabId = "info" | "summary" | "transactions";

const TABS: { id: TabId; label: string }[] = [
  { id: "info", label: "Info" },
  { id: "summary", label: "Summary" },
  { id: "transactions", label: "Transactions" },
];

function str(v: unknown): string {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

function obj(v: unknown): Record<string, unknown> | null {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : null;
}

function errorMessage(
  err: { response?: { data?: unknown } } | null | undefined,
  fallback: string
): string {
  const data = err?.response?.data;
  if (data && typeof data === "object" && "message" in data) {
    return String((data as { message?: string }).message);
  }
  return fallback;
}

function InfoRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-[#2C363E]/60 bg-[#1C2227]/80 px-4 py-3">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[#5C6B76]">
        {label}
      </p>
      <p className="mt-1 text-[13px] font-semibold text-[#E7EBEE] break-words">
        {value}
      </p>
    </div>
  );
}

const MerchantDetail: React.FC = () => {
  const { businessId = "" } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [tab, setTab] = useState<TabId>("info");

  // Fast path: the Users table hands the full record over on click.
  const merchantFromState = (location.state as { merchant?: MerchantRecord })
    ?.merchant;

  const {
    data: summaryData,
    isPending: summaryPending,
    isError: summaryIsError,
    error: summaryError,
    refetch: refetchSummary,
  } = useAdminBusinessSummaryQuery({
    businessId,
    enabled: Boolean(businessId),
  });

  /**
   * Fallback for refreshes and pasted links, where router state is gone: every
   * transaction row embeds a full `business` snapshot. Only fetched when the
   * state is missing, and only one row is needed.
   */
  const { data: fallbackTx, isPending: fallbackPending } =
    useAdminBusinessTransactionsQuery({
      page: 1,
      type: "deposits",
      limit: 1,
      businessId,
      enabled: Boolean(businessId) && !merchantFromState,
    });

  const merchant = useMemo<MerchantRecord | null>(() => {
    if (merchantFromState) return merchantFromState;
    const first = fallbackTx?.transactions?.[0];
    const business = first ? obj(first.business) : null;
    return (business as MerchantRecord) ?? null;
  }, [merchantFromState, fallbackTx?.transactions]);

  const details = obj(merchant?.details) ?? {};
  const merchantName = str(merchant?.name) || "Merchant";
  const infoUnavailable = !merchant && !fallbackPending;

  return (
    <div className="w-full h-screen flex flex-col overflow-hidden">
      <div className="w-full h-[31px] shrink-0 bg-[#1B1B1B] flex justify-center items-center">
        {IS_STAGING ? (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in Staging:{" "}
            <span>
              <a href={PROD_LINK} className="text-[#03C668]">
                Switch to Prod
              </a>
            </span>
          </h4>
        ) : (
          <h4 className="font-medium text-[#AEBCC6] text-[12px]">
            You are in PROD:{" "}
            <span>
              <a href={STAGING_LINK} className="text-[#DF4E4E]">
                Switch to Staging
              </a>
            </span>
          </h4>
        )}
      </div>
      <div className="flex flex-1 min-h-0 w-full flex-col lgss:flex-row overflow-hidden">
        <Sidebar />
        <div className="w-full lgss:w-10/12 flex-1 min-h-0 min-w-0 flex flex-col overflow-y-auto bg-[#13181B]">
          <Navbar />
          <div className="flex-1 px-5 pt-6 pb-24 mds:px-8 lgss:px-10 font-manrope bg-[#13181B] max-w-[1600px] mx-auto w-full">
            <nav
              className="flex items-center gap-1 text-[12px] text-[#5C6B76] mb-4"
              aria-label="Breadcrumb"
            >
              <button
                type="button"
                onClick={() => navigate("/users")}
                className="hover:text-[#AEBCC6] transition-colors"
              >
                Users
              </button>
              <MdOutlineKeyboardArrowRight className="text-base" aria-hidden />
              <span className="text-[#AEBCC6]">Merchant</span>
            </nav>

            <header className="mb-6">
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="font-sora font-semibold text-white text-xl tracking-tight capitalize">
                  {merchantName}
                </h1>
                {merchant?.status ? (
                  <span
                    className={`inline-flex rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${businessStatusBadgeClass(
                      merchant.status
                    )}`}
                  >
                    {str(merchant.status)}
                  </span>
                ) : null}
              </div>
              <p className="text-[13px] text-[#869AA9] mt-1">
                {str(merchant?.email) || businessId}
              </p>
            </header>

            <div
              className="mb-6 flex flex-wrap gap-2 border-b border-[#2C363E] pb-3"
              role="tablist"
              aria-label="Merchant sections"
            >
              {TABS.map((t) => {
                const active = tab === t.id;
                return (
                  <button
                    key={t.id}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(t.id)}
                    className={
                      active
                        ? "rounded-full border border-secondary_button bg-secondary_button/15 px-4 py-2 text-[12px] font-bold text-secondary_button"
                        : "rounded-full border border-[#2C363E] bg-[#161E25]/80 px-4 py-2 text-[12px] font-semibold text-[#AEBCC6] transition hover:border-[#3A4852] hover:text-white"
                    }
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>

            {tab === "info" && (
              <>
                {fallbackPending && !merchant && (
                  <div className="flex justify-center py-16 rounded-xl border border-[#2C363E] bg-[#161E25]/40">
                    <TailSpin color="#0B75C2" height={36} width={36} />
                  </div>
                )}

                {infoUnavailable && (
                  <div className="rounded-xl border border-[#2C363E] bg-[#161E25]/40 px-5 py-8 text-center">
                    <p className="text-[14px] font-semibold text-[#E7EBEE]">
                      Profile details unavailable
                    </p>
                    <p className="text-[13px] text-[#869AA9] mt-2 max-w-md mx-auto leading-relaxed">
                      This merchant has no transactions to read profile details
                      from. Open it from the Users list to see full details.
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate("/users")}
                      className="mt-4 rounded-lg border border-[#3A4852] bg-[#1C2227] px-4 py-2 text-[12px] font-semibold text-secondary_button hover:border-secondary_button hover:bg-[#273037]"
                    >
                      Go to Users
                    </button>
                  </div>
                )}

                {merchant && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 mb-10">
                    <InfoRow
                      label="Business name"
                      value={str(merchant.name) || "—"}
                    />
                    <InfoRow label="Email" value={str(merchant.email) || "—"} />
                    <InfoRow
                      label="Account type"
                      value={str(merchant.type) || "—"}
                    />
                    <InfoRow
                      label="Industry"
                      value={str(details.industry) || "Not provided"}
                    />
                    <InfoRow
                      label="Business size"
                      value={str(details.size) || "Not provided"}
                    />
                    <InfoRow
                      label="Country"
                      value={str(details.country) || "Not provided"}
                    />
                    <InfoRow
                      label="Address"
                      value={str(details.address) || "Not provided"}
                    />
                    <InfoRow
                      label="Description"
                      value={str(details.description) || "No description"}
                    />
                    <InfoRow
                      label="BN number"
                      value={str(merchant.bnNumber) || "Not provided"}
                    />
                    <InfoRow
                      label="Verified"
                      value={merchant.isVerified === true ? "Yes" : "No"}
                    />
                    <InfoRow
                      label="Created"
                      value={formatOrdinalDateTime(merchant.createdAt)}
                    />
                    <InfoRow
                      label="Business ID"
                      value={
                        <span className="font-mono text-[12px]">
                          {businessId}
                        </span>
                      }
                    />
                  </div>
                )}
              </>
            )}

            {tab === "summary" && (
              <>
                {summaryPending && (
                  <div
                    className="flex flex-col items-center justify-center py-24 gap-4 rounded-xl border border-[#2C363E] bg-[#161E25]/40"
                    aria-busy="true"
                  >
                    <TailSpin color="#0B75C2" height={40} width={40} />
                    <p className="text-[13px] text-[#869AA9]">
                      Loading summary…
                    </p>
                  </div>
                )}

                {summaryIsError && (
                  <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-4 text-[13px] text-red-100/95 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <span>
                      {errorMessage(
                        summaryError,
                        "Could not load this merchant's summary."
                      )}
                    </span>
                    <button
                      type="button"
                      onClick={() => refetchSummary()}
                      className="shrink-0 rounded-lg bg-red-500/20 px-4 py-2 text-[12px] font-semibold text-red-200 border border-red-500/40 hover:bg-red-500/30 transition"
                    >
                      Retry
                    </button>
                  </div>
                )}

                {!summaryPending && !summaryIsError && summaryData?.summary && (
                  <MerchantSummarySections
                    summary={summaryData.summary}
                    scope="merchant"
                  />
                )}
              </>
            )}

            {tab === "transactions" && (
              <MerchantTransactionsPanel
                businessId={businessId}
                exportTitle={`Merchant — ${merchantName}`}
                heading="Transactions"
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MerchantDetail;
